/**
 * useCreditCalculator Hook - Variable Credit Purchase System
 * Handles dynamic pricing with tiered discounts and minimum purchase requirements
 * Now supports multiple currencies via locale configuration
 */

import { useState, useCallback, useMemo } from 'react';
import { TIER_DISCOUNTS, formatPrice } from '../utils/localeConfig';

// Business Rules
export const PRICING_CONFIG = {
  MINIMUM_PURCHASE: 10,
  MAXIMUM_PURCHASE: 1000,
  QUICK_SELECT_OPTIONS: [10, 25, 50, 100, 250, 500]
};

/**
 * Calculate tier pricing for a quantity using locale rates
 * @param {number} quantity - Number of credits
 * @param {Object} locale - Locale configuration from LocaleContext
 * @returns {Object} Tier with calculated per-credit price
 */
const calculateTierRate = (quantity, locale) => {
  const tier = TIER_DISCOUNTS.find(t => quantity >= t.min && quantity <= t.max);
  if (!tier) {
    throw new Error('Invalid quantity - no tier found');
  }

  // Calculate per-credit rate with discount applied
  const discountMultiplier = (100 - tier.discount) / 100;
  const pricePerCredit = locale.basePerCreditRate * discountMultiplier;

  return {
    ...tier,
    pricePerCredit
  };
};

/**
 * Custom hook for credit purchase calculations
 * @param {Object} locale - Locale configuration object from LocaleContext
 */
export const useCreditCalculator = (locale) => {
  if (!locale || !locale.basePerCreditRate) {
    throw new Error('useCreditCalculator requires a valid locale with basePerCreditRate');
  }
  const [quantity, setQuantity] = useState(PRICING_CONFIG.MINIMUM_PURCHASE);

  // Calculate pricing details based on quantity
  const pricingDetails = useMemo(() => {
    const validQuantity = Math.max(PRICING_CONFIG.MINIMUM_PURCHASE, Math.min(quantity, PRICING_CONFIG.MAXIMUM_PURCHASE));

    // Get tier with calculated per-credit price for this locale
    const tier = calculateTierRate(validQuantity, locale);

    const pricePerCredit = tier.pricePerCredit;
    const totalPrice = validQuantity * pricePerCredit;
    const standardPrice = validQuantity * locale.basePerCreditRate;
    const savings = standardPrice - totalPrice;
    const savingsPercentage = tier.discount;

    return {
      quantity: validQuantity,
      pricePerCredit,
      totalPrice,
      savings: savings > 0 ? savings : 0,
      savingsPercentage,
      tier: tier.name,
      isMinimumMet: validQuantity >= PRICING_CONFIG.MINIMUM_PURCHASE,
      isMaximumExceeded: quantity > PRICING_CONFIG.MAXIMUM_PURCHASE
    };
  }, [quantity, locale]);

  // Validation results
  const validation = useMemo(() => {
    const errors = [];
    const warnings = [];

    const minPurchasePrice = PRICING_CONFIG.MINIMUM_PURCHASE * locale.basePerCreditRate;

    if (quantity < PRICING_CONFIG.MINIMUM_PURCHASE) {
      errors.push(`Minimum purchase is ${PRICING_CONFIG.MINIMUM_PURCHASE} credits (${formatPrice(minPurchasePrice, locale)})`);
    }

    if (quantity > PRICING_CONFIG.MAXIMUM_PURCHASE) {
      errors.push(`Maximum purchase is ${PRICING_CONFIG.MAXIMUM_PURCHASE} credits`);
    }

    if (quantity >= 20 && quantity < 26) {
      const additionalCost = (26 - quantity) * locale.basePerCreditRate;
      warnings.push(`Purchase ${26 - quantity} more credits to get 30% bulk discount (${formatPrice(additionalCost, locale)} more)`);
    }

    if (quantity >= 90 && quantity < 101) {
      const tier2Rate = locale.basePerCreditRate * 0.70; // 30% discount
      const additionalCost = (101 - quantity) * tier2Rate;
      warnings.push(`Purchase ${101 - quantity} more credits to get 40% premium discount (${formatPrice(additionalCost, locale)} more)`);
    }

    if (quantity >= 450 && quantity < 500) {
      const tier3Rate = locale.basePerCreditRate * 0.60; // 40% discount
      const additionalCost = (500 - quantity) * tier3Rate;
      warnings.push(`Purchase ${500 - quantity} more credits to get 50% super premium discount (${formatPrice(additionalCost, locale)} more)`);
    }

    return {
      isValid: errors.length === 0,
      errors,
      warnings,
      canProceed: errors.length === 0 && quantity >= PRICING_CONFIG.MINIMUM_PURCHASE
    };
  }, [quantity, locale]);

  // Set quantity with validation
  const setValidatedQuantity = useCallback((newQuantity) => {
    const numericQuantity = parseInt(newQuantity) || PRICING_CONFIG.MINIMUM_PURCHASE;
    setQuantity(numericQuantity);
  }, []);

  // Quick select handlers
  const selectQuickAmount = useCallback((amount) => {
    setQuantity(amount);
  }, []);

  // Get next tier suggestion
  const getNextTierSuggestion = useCallback(() => {
    if (quantity < 26) {
      const savingsAmount = quantity * (locale.basePerCreditRate * 0.10); // Additional 10% (30% - 20%)
      return {
        credits: 26,
        savings: `Save 30% (${formatPrice(savingsAmount, locale)} less)`,
        tier: 'Bulk'
      };
    }
    if (quantity < 101) {
      const savingsAmount = quantity * (locale.basePerCreditRate * 0.10); // Additional 10% (40% - 30%)
      return {
        credits: 101,
        savings: `Save 40% (${formatPrice(savingsAmount, locale)} less)`,
        tier: 'Premium'
      };
    }
    if (quantity < 500) {
      const savingsAmount = quantity * (locale.basePerCreditRate * 0.10); // Additional 10% (50% - 40%)
      return {
        credits: 500,
        savings: `Save 50% (${formatPrice(savingsAmount, locale)} less)`,
        tier: 'Super Premium'
      };
    }
    return null;
  }, [quantity, locale]);

  return {
    // State
    quantity,
    setQuantity: setValidatedQuantity,
    
    // Pricing
    pricingDetails,
    
    // Validation
    validation,
    
    // Utilities
    selectQuickAmount,
    getNextTierSuggestion,
    
    // Configuration
    config: PRICING_CONFIG
  };
};

/**
 * Utility function to calculate pricing for any quantity (no state)
 * @param {number} quantity - Number of credits
 * @param {Object} locale - Locale configuration object
 * @returns {Object} Pricing details or null if invalid
 */
export const calculatePricing = (quantity, locale) => {
  if (!locale || !locale.basePerCreditRate) {
    console.error('calculatePricing requires a valid locale with basePerCreditRate');
    return null;
  }

  const validQuantity = Math.max(PRICING_CONFIG.MINIMUM_PURCHASE, Math.min(quantity, PRICING_CONFIG.MAXIMUM_PURCHASE));
  const tier = calculateTierRate(validQuantity, locale);

  if (!tier) {
    return null;
  }

  const pricePerCredit = tier.pricePerCredit;
  const totalPrice = validQuantity * pricePerCredit;
  const standardPrice = validQuantity * locale.basePerCreditRate;
  const savings = standardPrice - totalPrice;

  return {
    quantity: validQuantity,
    pricePerCredit,
    totalPrice,
    savings: savings > 0 ? savings : 0,
    savingsPercentage: tier.discount,
    tier: tier.name
  };
};

/**
 * Format currency for display (using locale-aware formatting)
 * @param {number} amount - Amount to format
 * @param {Object} locale - Locale configuration object
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (amount, locale) => {
  return formatPrice(amount, locale);
};

/**
 * Format savings display
 * @param {number} savings - Savings amount
 * @param {number} percentage - Savings percentage
 * @param {Object} locale - Locale configuration object
 * @returns {string|null} Formatted savings string or null
 */
export const formatSavings = (savings, percentage, locale) => {
  if (savings <= 0) return null;
  return `Save ${formatCurrency(savings, locale)} (${percentage}%)`;
};